import { useCallback, useEffect, useMemo, useState } from 'react';
import { WbsLabelValueSearchResult } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { LabelSearchParams, useCPTLabelSearch } from '../../hooks';
import { DependantLabels } from '../schema';
import { CPT_LABEL_PAGE_COUNT } from '..';

type UseProjectChipData = {
  options: WbsLabelValueSearchResult[];
  handleSearch: (query: string | undefined) => void;
  label?: TimesheetLabel | null;
  loading: boolean;
  hasMore: boolean;
};

export const useProjects = (
  dependentLabels: DependantLabels,
  projectChipOpen: boolean
): UseProjectChipData => {
  const [options, setOptions] = useState<WbsLabelValueSearchResult[]>([]);
  const [lastSearch, setLastSearch] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);

  useEffect(() => {
    setLastSearch(undefined);
  }, [projectChipOpen]);

  const {
    searchHandler,
    data: { project: projectsData }
  } = useCPTLabelSearch();

  const loading = useMemo(() => Boolean(!projectsData), [projectsData]);
  const clientLabel = dependentLabels?.client?.label;
  const programLabel = dependentLabels?.program?.label;
  const projectDependUri = useMemo(() => {
    if (programLabel) {
      return { programUri: programLabel?.value?.idValue };
    } else if (clientLabel) {
      return { clientUri: clientLabel?.value?.idValue };
    } else return;
  }, [clientLabel, programLabel]);

  const searchParams = useMemo(
    () => (queryText?: string | null): LabelSearchParams => {
      return {
        context: 'project',
        query: queryText || undefined,
        ...projectDependUri
      };
    },
    [projectDependUri]
  );

  useEffect(() => {
    if (projectChipOpen && (clientLabel || programLabel)) {
      searchHandler(searchParams());
    }
  }, [searchHandler, clientLabel, projectChipOpen, searchParams, programLabel]);

  const handleSearch = useCallback(
    (queryText: string | undefined) => {
      if (lastSearch !== queryText) {
        searchHandler(searchParams(queryText));
      }
      setLastSearch(queryText);
    },
    [lastSearch, searchHandler, searchParams]
  );

  useEffect(() => {
    if (loading) {
      setOptions([]);
      setHasMore(false);
    } else if (projectsData) {
      const projects = projectsData!.wbsLabelValueSearch?.projects
        .items as WbsLabelValueSearchResult[];
      setOptions(projects);
      setHasMore(projects.length === CPT_LABEL_PAGE_COUNT);
    }
  }, [projectsData, loading]);

  return useMemo(
    () => ({
      options,
      label: dependentLabels.project.label,
      handleSearch,
      loading,
      hasMore
    }),
    [options, dependentLabels.project.label, handleSearch, loading, hasMore]
  );
};

export default useProjects;
